import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import MapboxMap, { Source, Layer, NavigationControl } from "react-map-gl/mapbox";
import Box from "@mui/material/Box";

import SideMenu from "./sideMenu";
import MunicipalityMapMenu from "../shared/municipalityMapMenu";
import { FIRE_HAZARD_COLOR_STOPS, MAP_STYLE } from "./constants";
import PropertyMarkers from "./markers/PropertyMarkers";
import { MapLayerOrderEnforcer } from "../shared/mapLayerOrder";
import { useLayerOpacities } from "../shared/useLayerOpacities";
import { useShow3dObjects } from "../shared/useShow3dObjects";
import {
  FIRE_TILES_HIGH_ZOOM,
  FIRE_TILES_LOW_ZOOM,
  MAPBOX_TILES,
  EARTHQUAKE_HAZARD_COLOR_STOPS,
  EARTHQUAKE_HAZARD_SOURCE_URL,
  EARTHQUAKE_TECTONIC_SOURCE_URL,
  EARTHQUAKE_FAULTS_SOURCE_URL,
  earthquakeHazardFillLayer,
  earthquakeTectonicPlatesLayer,
  earthquakeActiveFaultsLayer,
  fireGenericLayer,
  floodLakesLayer,
  floodHazardLayer,
  FLOOD_HAZARD_TILESETS,
  FLOOD_HAZARD_COLOR_STOPS_Z13,
  buildFloodHazardFillColor,
  floodRiversLayer,
} from "../risksMap/constants";

import MapSearch from "../shared/mapSearch";
import useSidebarWidth from "../../shared/hooks/useSidebarWidth";
import PortfolioSelectorDialog from "./portfolioSelectorDialog";

import "mapbox-gl/dist/mapbox-gl.css";
import { useQuery } from "@tanstack/react-query";
import { getConfig } from "../../shared/api";
import { useClient } from "../../contexts/ClientContext";
import { Skeleton } from "@mui/material";
import { getMunicipalityMapScope } from "../shared/mapInitialView";
import MunicipalityScopeLayers, {
  MUNICIPALITY_SCOPE_LAYER_IDS,
} from "../shared/municipalityScopeLayers";

const FALLBACK_INITIAL_VIEW_STATE = {
  longitude: 23.45498,
  latitude: 39.164454,
  zoom: 10,
};

const COMPLETED_PORTFOLIO_STATUS = "completed";

const getPortfolioId = (portfolio) => portfolio?.id ?? null;

const isPortfolioSelectable = (portfolio) =>
  portfolio?.status === COMPLETED_PORTFOLIO_STATUS;

const getFirstSelectablePortfolioId = (portfolios = []) => {
  const firstSelectable = portfolios.find(isPortfolioSelectable);
  return getPortfolioId(firstSelectable);
};

const getInitialPortfolioId = ({ portfolios, defaultPortfolioId }) => {
  if (!Array.isArray(portfolios) || portfolios.length === 0) {
    return null;
  }

  const defaultPortfolio = portfolios.find(
    (portfolio) => getPortfolioId(portfolio) === defaultPortfolioId,
  );

  if (isPortfolioSelectable(defaultPortfolio)) {
    return defaultPortfolioId;
  }

  return getFirstSelectablePortfolioId(portfolios);
};

function PortfolioMapInner({
  mapRef,
  markerInfo,
  currentProperties,
  selectedPortfolioId,
  onPortfolioChange,
  configData,
  initialViewState,
  mapInstanceKey,
  allowedMunicipalityIds,
}) {
  const [activeRisk, setActiveRisk] = useState("fire");
  const [selectedEarthquakeLayers, setSelectedEarthquakeLayers] = useState([
    "hazard",
  ]);
  const [selectedFloodLayers, setSelectedFloodLayers] = useState(["hazard"]);
  const [fireMapMode, setFireMapMode] = useState("hazard");
  const [activeMapStyle, setActiveMapStyle] = useState(MAP_STYLE.street);
  const [show3DMap, setShow3DMap] = useState(false);
  // 3D buildings come from the Mapbox Standard style (shown by default).
  const [show3DBuildings, setShow3DBuildings] = useState(true);
  const { layerOpacities, setLayerOpacity } = useLayerOpacities();

  // Satellite (Standard Satellite) has no 3D buildings, so force them off there
  // while keeping the user's toggle preference for when they return to street.
  useShow3dObjects(
    mapRef,
    show3DBuildings && activeMapStyle !== MAP_STYLE.satellite,
  );

  const setActiveMapStyleSafe = useCallback(
    (styleUrl) => {
      const map = mapRef.current?.getMap();
      // Clear fog so the transition doesn't query a missing fog state
      // fixes marker bug
      try {
        map?.setFog(null);
      } catch {}
      setActiveMapStyle(styleUrl);
    },
    [mapRef],
  );

  const onLocationSelect = useCallback(
    ({ lat, lng }) => {
      mapRef.current?.getMap()?.flyTo({ center: [lng, lat] });
    },
    [mapRef],
  );

  useEffect(() => {
    if (activeRisk === "fire") {
      setFireMapMode("hazard");
    }
  }, [activeRisk]);

  const isFireHazardModeActive =
    activeRisk === "fire" && fireMapMode === "hazard";

  const fireHazardFillColor = useMemo(() => {
    const [firstStop, ...remainingStops] = FIRE_HAZARD_COLOR_STOPS;
    const defaultColor = firstStop?.[1] ?? "#9efc9c";

    return [
      "step",
      ["coalesce", ["to-number", ["get", "risk_fire"]], 0],
      defaultColor,
      ...remainingStops.flat(),
    ];
  }, []);

  const showEarthquakeHazardLayer =
    activeRisk === "earthquake" && selectedEarthquakeLayers.includes("hazard");
  const showEarthquakeTectonicLayer =
    activeRisk === "earthquake" &&
    selectedEarthquakeLayers.includes("tectonicPlates");
  const showEarthquakeFaultsLayer =
    activeRisk === "earthquake" &&
    selectedEarthquakeLayers.includes("activeFaults");

  const showFloodHazardLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("hazard");
  const showFloodRiversLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("rivers");
  const showFloodLakesLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("lakes");

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 64px)",
        position: "relative",
      }}
    >
      <MapSearch
        onLocationSelect={onLocationSelect}
        placeholderKey="portfolioMap.mapSearchPlaceholder"
      />

      <PortfolioSelectorDialog
        selectedPortfolioId={selectedPortfolioId}
        onPortfolioChange={onPortfolioChange}
        configData={configData}
      />

      <MapboxMap
        key={mapInstanceKey}
        ref={mapRef}
        style={{ width: "100%", height: "100%" }}
        mapStyle={activeMapStyle}
        projection="mercator"
        terrain={
          show3DMap ? { source: "mapbox-dem", exaggeration: 1.2 } : undefined
        }
        initialViewState={initialViewState}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        interactiveLayerIds={["municipalities-layer-fill"]}
        maxPitch={85}
      >
        <NavigationControl position="top-right" />

        <MapLayerOrderEnforcer />

        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />

        {/* Property markers */}
        {currentProperties?.length > 0 && (
          <PropertyMarkers
            properties={currentProperties}
            markerInfo={markerInfo}
            mapRef={mapRef}
          />
        )}

        {activeRisk === "fire" &&
          fireMapMode === "terrain" &&
          FIRE_TILES_HIGH_ZOOM.map((tileset, index) => (
            <Source
              id={`fire-${index}`}
              key={tileset.url}
              type="vector"
              url={tileset.url}
            >
              <Layer
                {...fireGenericLayer}
                id={`fire-layer-${index}`}
                source-layer={tileset["source-layer"]}
                minzoom={11}
                paint={{
                  ...fireGenericLayer.paint,
                  "fill-opacity": layerOpacities.fire,
                }}
              />
            </Source>
          ))}

        {activeRisk === "fire" &&
          fireMapMode === "terrain" &&
          FIRE_TILES_LOW_ZOOM.map((tileset, index) => (
            <Source
              id={`fire-low-${index}`}
              key={tileset.url}
              type="vector"
              url={tileset.url}
            >
              <Layer
                {...fireGenericLayer}
                id={`fire-low-layer-${index}`}
                source-layer={tileset["source-layer"]}
                maxzoom={11}
                paint={{
                  ...fireGenericLayer.paint,
                  "fill-opacity": layerOpacities.fire,
                }}
              />
            </Source>
          ))}

        {isFireHazardModeActive && (
          <Source
            id="fire-hazard-source"
            url="mapbox://georgemouts.7l4wxdqb"
            type="vector"
          >
            <Layer
              id="fire-hazard-layer-fill"
              type="fill"
              source-layer="skiathos_grid_risk"
              paint={{
                "fill-color": fireHazardFillColor,
                "fill-opacity": layerOpacities.fire,
                "fill-outline-color": "rgba(0, 0, 0, 0)",
              }}
            />
          </Source>
        )}

        {activeRisk === "flood" && (
          <>
            {showFloodHazardLayer &&
              FLOOD_HAZARD_TILESETS.map((tileset) => (
                <Source
                  key={tileset.id}
                  id={tileset.id}
                  type="vector"
                  url={tileset.url}
                >
                  <Layer
                    {...floodHazardLayer}
                    id={`${tileset.id}-layer`}
                    minzoom={tileset.minzoom}
                    maxzoom={tileset.maxzoom}
                    paint={{
                      ...floodHazardLayer.paint,
                      "fill-color": buildFloodHazardFillColor(
                        tileset.property,
                        tileset.colorStops,
                      ),
                      "fill-opacity": layerOpacities.flood,
                      ...(tileset.useTransparentOutline
                        ? { "fill-outline-color": "rgba(0, 0, 0, 0)" }
                        : {}),
                    }}
                  />
                </Source>
              ))}
            {showFloodRiversLayer && (
              <Source id="flood-rivers" type="vector" url={MAPBOX_TILES.rivers}>
                <Layer
                  {...floodRiversLayer}
                  paint={{
                    ...floodRiversLayer.paint,
                    "line-opacity": layerOpacities.flood,
                  }}
                />
              </Source>
            )}
            {showFloodLakesLayer && (
              <Source id="flood-lakes" type="vector" url={MAPBOX_TILES.lakes}>
                <Layer
                  {...floodLakesLayer}
                  paint={{
                    ...floodLakesLayer.paint,
                    "fill-opacity": layerOpacities.flood,
                  }}
                />
              </Source>
            )}
          </>
        )}

        {showEarthquakeHazardLayer && (
          <Source
            id="earthquake-risk"
            type="vector"
            url={EARTHQUAKE_HAZARD_SOURCE_URL}
          >
            <Layer
              {...earthquakeHazardFillLayer}
              paint={{
                ...earthquakeHazardFillLayer.paint,
                "fill-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        {showEarthquakeTectonicLayer && (
          <Source
            id="earthquake-tectonic-plates"
            type="vector"
            url={EARTHQUAKE_TECTONIC_SOURCE_URL}
          >
            <Layer
              {...earthquakeTectonicPlatesLayer}
              paint={{
                ...earthquakeTectonicPlatesLayer.paint,
                "line-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        {showEarthquakeFaultsLayer && (
          <Source
            id="earthquake-active-faults"
            type="vector"
            url={EARTHQUAKE_FAULTS_SOURCE_URL}
          >
            <Layer
              {...earthquakeActiveFaultsLayer}
              paint={{
                ...earthquakeActiveFaultsLayer.paint,
                "line-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        <MunicipalityScopeLayers
          allowedMunicipalityIds={allowedMunicipalityIds}
          boundarySourceId={
            MUNICIPALITY_SCOPE_LAYER_IDS.portfolio.boundarySourceId
          }
          boundaryFillLayerId={
            MUNICIPALITY_SCOPE_LAYER_IDS.portfolio.boundaryFillLayerId
          }
          boundaryLineLayerId={
            MUNICIPALITY_SCOPE_LAYER_IDS.portfolio.boundaryLineLayerId
          }
          maskSourceId={MUNICIPALITY_SCOPE_LAYER_IDS.portfolio.maskSourceId}
          maskLayerId={MUNICIPALITY_SCOPE_LAYER_IDS.portfolio.maskLayerId}
          boundaryLineWidth={1}
          maskFillOpacity={0.96}
        />
      </MapboxMap>

      <MunicipalityMapMenu
        setActiveRisk={setActiveRisk}
        activeRisk={activeRisk}
        fireMapMode={fireMapMode}
        setFireMapMode={setFireMapMode}
        setActiveMapStyle={setActiveMapStyleSafe}
        activeMapStyle={activeMapStyle}
        setShow3DMap={setShow3DMap}
        show3DMap={show3DMap}
        setShow3DBuildings={setShow3DBuildings}
        show3DBuildings={show3DBuildings}
        selectedEarthquakeLayers={selectedEarthquakeLayers}
        setSelectedEarthquakeLayers={setSelectedEarthquakeLayers}
        selectedFloodLayers={selectedFloodLayers}
        setSelectedFloodLayers={setSelectedFloodLayers}
        mapStyle={MAP_STYLE}
        fireHazardColorStops={FIRE_HAZARD_COLOR_STOPS}
        earthquakeHazardColorStops={EARTHQUAKE_HAZARD_COLOR_STOPS}
        floodHazardColorStops={FLOOD_HAZARD_COLOR_STOPS_Z13}
        layerOpacities={layerOpacities}
        setLayerOpacity={setLayerOpacity}
      />
    </div>
  );
}

function MunicipalityPortfolioMap({ configData, clientInfo }) {
  const mapRef = useRef();

  const { width: drawerWidth, setWidth: setDrawerWidth } = useSidebarWidth({
    storageKey: "artemis.sidebarWidth.municipalityPortfolioMap",
    minWidth: 460,
  });

  const portfolios = useMemo(
    () => configData?.portfolios?.available ?? [],
    [configData?.portfolios?.available],
  );
  const defaultPortfolioId = configData?.portfolios?.default ?? null;

  const [menuOpen, setMenuOpen] = React.useState(true);

  const [markerInfo, setMarkerInfo] = useState(null);
  const [currentProperties, setCurrentProperties] = useState([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(() =>
    getInitialPortfolioId({
      portfolios,
      defaultPortfolioId,
    }),
  );

  useEffect(() => {
    setSelectedPortfolioId((previousSelectedPortfolioId) => {
      const hasPreviousSelection = portfolios.some(
        (portfolio) =>
          getPortfolioId(portfolio) === previousSelectedPortfolioId &&
          isPortfolioSelectable(portfolio),
      );

      if (hasPreviousSelection) {
        return previousSelectedPortfolioId;
      }

      return getInitialPortfolioId({
        portfolios,
        defaultPortfolioId,
      });
    });
  }, [portfolios, defaultPortfolioId]);

  const handlePortfolioChange = useCallback((nextPortfolioId) => {
    setSelectedPortfolioId(nextPortfolioId);
    setMarkerInfo(null);
    setCurrentProperties([]);
  }, []);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const municipalityMapScope = useMemo(
    () =>
      getMunicipalityMapScope({
        clientInfo,
        fallbackViewState: FALLBACK_INITIAL_VIEW_STATE,
        mapKeyPrefix: "municipality-portfolio-map",
      }),
    [clientInfo],
  );

  useEffect(() => {
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.getMap().resize();
      }
    }, 300); // matches your transition duration
  }, [menuOpen, drawerWidth]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${menuOpen ? drawerWidth : 0}px)` },
          transition: "margin 0.3s",
          marginRight: menuOpen ? `${drawerWidth}px` : 0,
        }}
      >
        <PortfolioMapInner
          mapRef={mapRef}
          markerInfo={markerInfo}
          currentProperties={currentProperties}
          selectedPortfolioId={selectedPortfolioId}
          onPortfolioChange={handlePortfolioChange}
          configData={configData}
          initialViewState={municipalityMapScope.initialViewState}
          mapInstanceKey={municipalityMapScope.mapInstanceKey}
          allowedMunicipalityIds={municipalityMapScope.allowedMunicipalityIds}
        />
      </Box>

      <SideMenu
        open={menuOpen}
        handleToggle={handleMenuToggle}
        mapRef={mapRef}
        setMarkerInfo={setMarkerInfo}
        setCurrentProperties={setCurrentProperties}
        selectedPortfolioId={selectedPortfolioId}
        width={drawerWidth}
        onResize={setDrawerWidth}
      />
    </Box>
  );
}

const MunicipalityPortfolioMapWithConfig = () => {
  const { clientInfo } = useClient();
  const { data: configData, isLoading: isConfigLoading } = useQuery({
    queryKey: ["config", clientInfo?.clientId],
    queryFn: () => getConfig({ clientId: clientInfo?.clientId }),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!clientInfo?.clientId,
  });

  // Show skeletons while config is loading
  if (isConfigLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "calc(100vh - 64px)",
          position: "relative",
        }}
      >
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    );
  }

  return (
    <MunicipalityPortfolioMap configData={configData} clientInfo={clientInfo} />
  );
};

export default MunicipalityPortfolioMapWithConfig;
